import { useState } from "react"

import { DeleteForever, Warning, Visibility, Download } from '@mui/icons-material'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  TextField,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Paper,
} from '@mui/material'

import { gdprService, type User, type GdprDeletionPreview, type GdprDeletionSummary } from "../../api"
import { downloadUserDataExport } from "../../lib/export-utils"

type GdprDeleteDialogOptions = {
  user: User
  onDeleteSuccess: () => void
}

type DialogStep = 'closed' | 'preview' | 'confirm' | 'completed'

export function GdprDeleteDialog({ user, onDeleteSuccess }: GdprDeleteDialogOptions) {
  const [step, setStep] = useState<DialogStep>('closed')
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [confirmationText, setConfirmationText] = useState('')
  const [preview, setPreview] = useState<GdprDeletionPreview | null>(null)
  const [deletionSummary, setDeletionSummary] = useState<GdprDeletionSummary | null>(null)
  const [error, setError] = useState<string | null>(null)

  const confirmationRequired = 'DELETE'
  const isConfirmed = confirmationText === confirmationRequired

  const handleOpen = async () => {
    setStep('preview')
    setError(null)
    setIsLoadingPreview(true)

    try {
      const previewData = await gdprService.previewUserDeletion(user.id)
      setPreview(previewData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load deletion preview')
    } finally {
      setIsLoadingPreview(false)
    }
  }

  const handleClose = () => {
    if (!isDeleting) {
      setStep('closed')
      setConfirmationText('')
      setError(null)
      setPreview(null)
      setDeletionSummary(null)
    }
  }

  const handleProceedToConfirm = () => {
    setStep('confirm')
    setConfirmationText('')
  }

  const handleDelete = async () => {
    try {
      setIsDeleting(true)
      setError(null)
      const summary = await gdprService.deleteUserForGdpr(user.id)
      setDeletionSummary(summary)
      setStep('completed')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleConfirmAndClose = () => {
    handleClose()
    onDeleteSuccess()
  }

  const handleExportData = async () => {
    try {
      setIsExporting(true)
      setError(null)
      
      await downloadUserDataExport(user.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export user data')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <>
      <Button
        size="small"
        variant="contained"
        color="error"
        startIcon={<DeleteForever />}
        onClick={(e) => {
          e.stopPropagation()
          handleOpen()
        }}
        sx={{
          fontSize: '0.75rem',
          fontWeight: 500,
          minWidth: 'auto',
          px: 1.5,
          py: 0.5,
          height: 28,
        }}
      >
        GDPR Delete
      </Button>

      <Dialog
        open={step !== 'closed'}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={isDeleting}
      >
        {step === 'preview' && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Visibility />
              Preview User Deletion
            </DialogTitle>

            <DialogContent>
              {isLoadingPreview ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Alert severity="error">{error}</Alert>
              ) : preview ? (
                <>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2" fontWeight="bold" gutterBottom>
                      Review what will be deleted
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      This preview shows all data that will be permanently removed from the system.
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, fontSize: '0.875rem', color: 'info.dark' }}>
                      💡 Tip: Export the data first. The ZIP file is password-protected with: <strong>AURA</strong>
                    </Typography>
                  </Alert>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      User Information:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Email: {preview.userEmail}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Name: {preview.firstName} {preview.lastName}
                    </Typography>
                  </Box>

                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="error.main">
                      Total Records to Delete: {preview.totalRecordsCount}
                    </Typography>
                  </Paper>

                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Data breakdown:
                  </Typography>
                  <List dense disablePadding>
                    <ListItem disablePadding>
                      <ListItemText
                        primary="User Account"
                        secondary="1 record"
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                    {preview.profilesCount > 0 && (
                      <ListItem disablePadding>
                        <ListItemText
                          primary="Health Profiles"
                          secondary={`${preview.profilesCount} record(s)`}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    )}
                    {preview.ordersCount > 0 && (
                      <ListItem disablePadding>
                        <ListItemText
                          primary="Lab Orders"
                          secondary={`${preview.ordersCount} record(s)`}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    )}
                    {preview.aiSessionsCount > 0 && (
                      <ListItem disablePadding>
                        <ListItemText
                          primary="AI Sessions"
                          secondary={`${preview.aiSessionsCount} record(s)`}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    )}
                    {preview.aiResponsesCount > 0 && (
                      <ListItem disablePadding>
                        <ListItemText
                          primary="AI Responses"
                          secondary={`${preview.aiResponsesCount} record(s)`}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    )}
                    {preview.aiQcReviewsCount > 0 && (
                      <ListItem disablePadding>
                        <ListItemText
                          primary="QC Reviews"
                          secondary={`${preview.aiQcReviewsCount} record(s)`}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    )}
                    {preview.eventLogsCount > 0 && (
                      <ListItem disablePadding>
                        <ListItemText
                          primary="Event Logs"
                          secondary={`${preview.eventLogsCount} record(s)`}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    )}
                    {preview.emailAuthCodesCount > 0 && (
                      <ListItem disablePadding>
                        <ListItemText
                          primary="Email Auth Codes"
                          secondary={`${preview.emailAuthCodesCount} record(s)`}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    )}
                  </List>
                </>
              ) : null}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
              <Button onClick={handleClose} variant="outlined">
                Cancel
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  onClick={handleExportData}
                  variant="outlined"
                  disabled={isLoadingPreview || isExporting || !!error}
                  startIcon={isExporting ? <CircularProgress size={16} /> : <Download />}
                >
                  {isExporting ? 'Exporting...' : 'Export Data'}
                </Button>
                <Button
                  onClick={handleProceedToConfirm}
                  variant="contained"
                  color="error"
                  disabled={isLoadingPreview || !!error}
                  startIcon={<Warning />}
                >
                  Proceed to Delete
                </Button>
              </Box>
            </DialogActions>
          </>
        )}

        {step === 'confirm' && preview && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Warning color="error" />
              Confirm Permanent Deletion
            </DialogTitle>

            <DialogContent>
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  This action cannot be undone!
                </Typography>
                <Typography variant="body2">
                  {preview.totalRecordsCount} records will be permanently deleted from the system.
                </Typography>
              </Alert>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Deleting:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {preview.userEmail}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  To confirm deletion, type <code style={{
                    backgroundColor: '#f5f5f5',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontWeight: 'bold'
                  }}>DELETE</code> below:
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value)}
                  placeholder="Type DELETE to confirm"
                  disabled={isDeleting}
                  error={confirmationText.length > 0 && !isConfirmed}
                  helperText={confirmationText.length > 0 && !isConfirmed ? 'Text must match exactly' : ''}
                  sx={{ mt: 1 }}
                  autoFocus
                />
              </Box>

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button
                onClick={() => setStep('preview')}
                variant="outlined"
                disabled={isDeleting}
              >
                Back
              </Button>
              <Button
                onClick={handleDelete}
                variant="contained"
                color="error"
                disabled={!isConfirmed || isDeleting}
                startIcon={isDeleting ? undefined : <DeleteForever />}
              >
                {isDeleting ? 'Deleting...' : 'Delete Permanently'}
              </Button>
            </DialogActions>
          </>
        )}

        {step === 'completed' && deletionSummary && (
          <>
            <DialogTitle>
              Deletion Complete
            </DialogTitle>

            <DialogContent>
              <Alert severity="success" sx={{ mb: 3 }}>
                User data has been permanently deleted from the system.
              </Alert>

              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Deletion Summary:
              </Typography>

              <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  User: {deletionSummary.userEmail}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  User ID: {deletionSummary.userId}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Deleted At: {new Date(deletionSummary.deletedAt).toLocaleString()}
                </Typography>
              </Box>

              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Records Deleted:
              </Typography>

              <List dense>
                <ListItem>
                  <ListItemText
                    primary="Profiles"
                    secondary={deletionSummary.profilesDeleted}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Orders"
                    secondary={deletionSummary.ordersDeleted}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="AI Sessions"
                    secondary={deletionSummary.aiSessionsDeleted}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="AI Responses"
                    secondary={deletionSummary.aiResponsesDeleted}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="QC Reviews"
                    secondary={deletionSummary.aiQcReviewsDeleted}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Event Logs"
                    secondary={deletionSummary.eventLogsDeleted}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Email Auth Codes"
                    secondary={deletionSummary.emailAuthCodesDeleted}
                  />
                </ListItem>
              </List>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button
                onClick={handleConfirmAndClose}
                variant="contained"
              >
                Done
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  )
}
